import { awsRum } from "../rum/cloudwatchRum";

/** The backend enums whose unrecognized values are reported here. Labels the RUM report and keys its dedupe. */
export type StatusKind =
  | "OrderStatus"
  | "PublicOrderStatus"
  | "PrintAssetStatus"
  | "OrderItemStatus"
  | "PaymentStatus"
  | "UploadedFileStatus"
  | "CancellationOutcome";

const reportedUnrecognized = new Set<string>();

// Past this many distinct values the reporting stops, so memory and RUM volume stay bounded by us rather
// than by whatever the backend sends — a status that starts carrying an id suffix would otherwise be
// unbounded in both. Comfortably above the combined size of every enum above.
const REPORT_LIMIT = 200;

// Reports an unrecognized status to RUM once per (kind, value) per session — deduped so a value appearing
// across many table rows and re-renders can't spam the JS-error rate alarm. Routed through recordError so
// the gap still lands on that alarm: a status the backend emits but this bundle doesn't know is something
// we want to be notified about, not silently degrade.
export function reportUnrecognizedStatus(kind: StatusKind, status: string): void {
  const key = `${kind}:${status}`;
  if (reportedUnrecognized.has(key) || reportedUnrecognized.size >= REPORT_LIMIT) return;
  reportedUnrecognized.add(key);
  try {
    awsRum?.recordError(new Error(`Unrecognized ${kind} value from backend: ${status}`));
  } catch {
    // Telemetry must never crash the cell — this fallback exists precisely to avoid a render crash.
  }
}

// Resolves the display name for a status, falling back to the raw value for one this build doesn't know so
// the field reads something rather than nothing. Reports the gap to RUM (deduped).
export function resolveStatusName<T extends string>(names: Record<T, string>, kind: StatusKind, status: T): string {
  const known: string | undefined = names[status];
  if (known !== undefined) return known;
  reportUnrecognizedStatus(kind, status);
  return status;
}

/** Titles an underscored enum value: `NO_PAYMENT_STAFF_ORDER` reads "No Payment Staff Order". */
export function titleCaseStatus(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
